/**
 * DateRangeSelector Component - Phase 5 Implementation
 * Date range picker with preset options for report filtering
 * Supports common business date ranges and custom selection
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Calendar } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { DateRange } from '../../types/reports';
import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  format
} from 'date-fns';

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
}

interface DatePreset {
  id: string;
  label: string;
  getValue: () => DateRange;
}

const createPresets = (): DatePreset[] => {
  const now = new Date();
  
  return [
    {
      id: 'today',
      label: 'Today',
      getValue: () => ({
        start: startOfDay(now),
        end: endOfDay(now),
        preset: 'today'
      })
    },
    {
      id: 'yesterday',
      label: 'Yesterday',
      getValue: () => {
        const yesterday = subDays(now, 1);
        return {
          start: startOfDay(yesterday),
          end: endOfDay(yesterday),
          preset: 'custom'
        };
      }
    },
    {
      id: 'last7days',
      label: 'Last 7 Days',
      getValue: () => ({
        start: startOfDay(subDays(now, 6)),
        end: endOfDay(now),
        preset: 'week'
      })
    },
    {
      id: 'last30days',
      label: 'Last 30 Days',
      getValue: () => ({
        start: startOfDay(subDays(now, 29)),
        end: endOfDay(now),
        preset: 'month'
      })
    },
    {
      id: 'thisweek',
      label: 'This Week',
      getValue: () => ({
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
        preset: 'week'
      })
    },
    {
      id: 'lastweek',
      label: 'Last Week',
      getValue: () => {
        const lastWeek = subWeeks(now, 1);
        return {
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 }),
          preset: 'week'
        };
      }
    },
    {
      id: 'thismonth',
      label: 'This Month',
      getValue: () => ({
        start: startOfMonth(now),
        end: endOfMonth(now),
        preset: 'month'
      })
    },
    {
      id: 'lastmonth',
      label: 'Last Month',
      getValue: () => {
        const lastMonth = subMonths(now, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth),
          preset: 'month'
        };
      }
    },
    {
      id: 'thisquarter',
      label: 'This Quarter',
      getValue: () => ({
        start: startOfQuarter(now),
        end: endOfQuarter(now),
        preset: 'quarter'
      })
    },
    {
      id: 'lastquarter',
      label: 'Last Quarter',
      getValue: () => {
        const lastQuarter = subQuarters(now, 1);
        return {
          start: startOfQuarter(lastQuarter),
          end: endOfQuarter(lastQuarter),
          preset: 'quarter'
        };
      }
    },
    {
      id: 'thisyear',
      label: 'This Year',
      getValue: () => ({
        start: startOfYear(now),
        end: endOfYear(now),
        preset: 'year'
      })
    },
    {
      id: 'lastyear',
      label: 'Last Year',
      getValue: () => {
        const lastYear = subYears(now, 1);
        return {
          start: startOfYear(lastYear),
          end: endOfYear(lastYear),
          preset: 'year'
        };
      }
    }
  ];
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className
}) => {
  const [isCustom, setIsCustom] = useState(false);
  const presets = createPresets();

  const getCurrentPresetId = () => {
    // Try to find matching preset
    for (const preset of presets) {
      const presetRange = preset.getValue();
      if (
        Math.abs(presetRange.start.getTime() - value.start.getTime()) < 60000 && // within 1 minute
        Math.abs(presetRange.end.getTime() - value.end.getTime()) < 60000
      ) {
        return preset.id;
      }
    }
    return 'custom';
  };

  const handlePresetChange = (presetId: string) => {
    if (presetId === 'custom') {
      setIsCustom(true);
      return;
    }

    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      onChange(preset.getValue());
      setIsCustom(false);
    }
  };

  const formatDateRange = (range: DateRange) => {
    const startStr = format(range.start, 'MMM d, yyyy');
    const endStr = format(range.end, 'MMM d, yyyy');
    
    if (startStr === endStr) {
      return startStr;
    }
    
    return `${startStr} - ${endStr}`;
  };

  const currentPresetId = getCurrentPresetId();

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <Select
        value={currentPresetId}
        onValueChange={handlePresetChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <div className="flex items-center space-x-2 flex-1">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <SelectValue>
              {currentPresetId === 'custom' 
                ? formatDateRange(value)
                : presets.find(p => p.id === currentPresetId)?.label || 'Select range'
              }
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {presets.map((preset) => (
            <SelectItem key={preset.id} value={preset.id}>
              <div className="flex flex-col">
                <span>{preset.label}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDateRange(preset.getValue())}
                </span>
              </div>
            </SelectItem>
          ))}
          <SelectItem value="custom">
            <div className="flex flex-col">
              <span>Custom Range</span>
              <span className="text-xs text-muted-foreground">
                Choose specific dates
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      {/* Note: In a real implementation, you would add a date picker dialog here for custom ranges */}
      {isCustom && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCustom(false)}
          className="ml-2"
        >
          Cancel
        </Button>
      )}
    </div>
  );
};

export default DateRangeSelector;